import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { queryKeys } from '@/lib/api/queryKeys';
import { settingsRepository } from '../repository';
import { useSettingsStore } from '@/store/settingsStore';
import { useAuthStore } from '@/store/authStore';
import { env } from '@/lib/config/env';
import type { StoreSettings } from '@/types/domain';
import type { ApiError } from '@/types/common';

export function useStoreSettings() {
  const setSettings = useSettingsStore((s) => s.setSettings);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  const query = useQuery({
    queryKey: queryKeys.settings.all,
    queryFn: () => settingsRepository.get(),
    enabled: isAuthenticated && !env.useMockApi,
  });

  useEffect(() => {
    if (query.data) setSettings(query.data);
  }, [query.data, setSettings]);

  return query;
}

export function useUpdateSettings() {
  const qc = useQueryClient();
  const setSettings = useSettingsStore((s) => s.setSettings);

  return useMutation({
    mutationFn: (data: StoreSettings): Promise<StoreSettings> => settingsRepository.update(data),
    onSuccess: (data) => {
      setSettings(data);
      qc.setQueryData(queryKeys.settings.all, data);
      toast.success('Settings saved');
    },
    onError: (err: ApiError) => toast.error(err?.message ?? 'Failed to save settings'),
  });
}

export function useUploadLogo() {
  const qc = useQueryClient();
  const setSettings = useSettingsStore((s) => s.setSettings);

  return useMutation({
    mutationFn: (file: File) => settingsRepository.uploadLogo(file),
    onSuccess: (data) => {
      setSettings(data);
      qc.setQueryData(queryKeys.settings.all, data);
      toast.success('Logo updated');
    },
    onError: (err: ApiError) => toast.error(err?.message ?? 'Failed to upload logo'),
  });
}
